import React, { useState, useEffect } from 'react';
import { AppDrawer } from '../ui/AppDrawer';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { IntegerQuantityInput } from '../IntegerQuantityInput';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Loader2, Settings2 } from 'lucide-react';
import { toast } from 'sonner';
import { manualAdjustmentsAPI } from '../../lib/api';

const ADJUSTMENT_TYPES = [
  { value: 'increase', label: 'Increase (+)' },
  { value: 'decrease', label: 'Decrease (-)' },
  { value: 'set_exact', label: 'Set Exact (=)' },
];

const ADJUSTMENT_REASONS = [
  { value: 'Correction', label: 'Correction' },
  { value: 'Damaged', label: 'Damaged' },
  { value: 'Lost', label: 'Lost' },
  { value: 'Found', label: 'Found' },
  { value: 'Audit Count', label: 'Audit Count' },
  { value: 'Other', label: 'Other' },
];

export const ManualAdjustmentModal = ({ 
  open, 
  onClose,
  locationType,
  locationId,
  locationName,
  itemId,
  itemName,
  currentQuantity = 0,
  defaultAdjustmentType = null,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [adjustmentType, setAdjustmentType] = useState('increase');
  const [quantity, setQuantity] = useState('1');
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');

  useEffect(() => {
    if (open) {
      setAdjustmentType(defaultAdjustmentType || 'increase');
      setQuantity('1');
      setReason('');
      setNote('');
    }
  }, [open, defaultAdjustmentType]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!reason) { toast.error('Please select a reason'); return; }
    const qty = parseInt(quantity);
    if (adjustmentType === 'set_exact') {
      if (qty < 0) { toast.error('Quantity cannot be negative'); return; }
    } else {
      if (qty < 1) { toast.error('Quantity must be at least 1'); return; }
    }
    if (adjustmentType === 'decrease' && currentQuantity - qty < 0) {
      toast.error(`Cannot decrease below 0. Current: ${currentQuantity}`);
      return;
    }

    setSubmitting(true);
    try {
      const response = await manualAdjustmentsAPI.create({
        location_type: locationType,
        location_id: locationId,
        item_id: itemId,
        adjustment_type: adjustmentType,
        quantity: qty,
        reason: reason,
        note: note || null,
      });
      toast.success('Inventory adjusted');
      onClose(true, response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to adjust inventory');
    } finally {
      setSubmitting(false);
    }
  };

  const qty = parseInt(quantity) || 0;
  let previewQty = currentQuantity;
  if (adjustmentType === 'increase') previewQty = currentQuantity + qty;
  else if (adjustmentType === 'decrease') previewQty = currentQuantity - qty;
  else if (adjustmentType === 'set_exact') previewQty = qty;
  const minQty = adjustmentType === 'set_exact' ? 0 : 1;
  const isDirty = reason !== '' || note !== '';

  return (
    <AppDrawer
      open={open}
      onOpenChange={(nextOpen) => { if (!nextOpen) onClose(false); }}
      title={
        <span className="flex items-center gap-2">
          <Settings2 className="w-5 h-5" />
          Adjust Inventory
        </span>
      }
      description={`Manually adjust ${itemName || 'item'} at ${locationName || 'location'}`}
      size="md"
      isDirty={isDirty}
      data-testid="manual-adjustment-drawer"
      footer={
        <>
          <Button variant="outline" onClick={() => onClose(false)} disabled={submitting} data-testid="adjustment-cancel-btn">
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={submitting || previewQty < 0} data-testid="adjustment-submit-btn">
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adjusting...</>
            ) : (
              'Apply Adjustment'
            )}
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Current Quantity Display */}
        <div className="bg-muted p-3 rounded-lg">
          <p className="text-sm text-muted-foreground">Current Quantity</p>
          <p className="text-2xl font-bold text-foreground">{currentQuantity}</p>
        </div>

        {/* Adjustment Type */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Adjustment Type</Label>
          <Select value={adjustmentType} onValueChange={setAdjustmentType}>
            <SelectTrigger className="h-12" data-testid="adjustment-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ADJUSTMENT_TYPES.map((type) => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Quantity */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Quantity</Label>
          <IntegerQuantityInput min={minQty} value={quantity}
            onChange={(val) => setQuantity(val)} className="h-12 text-lg font-mono"
            data-testid="adjustment-quantity-input" />
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Result:</span>
            <span className={`font-mono font-bold ${previewQty < 0 ? 'text-destructive' : 'text-foreground'}`}>
              {currentQuantity} → {previewQty}
            </span>
            {previewQty < 0 && <span className="text-destructive text-xs">(Invalid)</span>}
          </div>
        </div>

        {/* Reason (Required) */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Reason <span className="text-destructive">*</span></Label>
          <Select value={reason} onValueChange={setReason}>
            <SelectTrigger className="h-12" data-testid="reason-select">
              <SelectValue placeholder="Select reason" />
            </SelectTrigger>
            <SelectContent>
              {ADJUSTMENT_REASONS.map((r) => (
                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Optional Notes */}
        <div className="space-y-2">
          <Label className="text-sm font-bold uppercase tracking-wide">Notes (optional)</Label>
          <Textarea placeholder="Additional details about this adjustment"
            value={note} onChange={(e) => setNote(e.target.value)}
            rows={2} className="resize-none" data-testid="adjustment-note-input" />
        </div>
      </div>
    </AppDrawer>
  );
};
